import { ReactElement, ReactNode } from 'react';

import { randomUUID } from '@betterment/core';
import { echoLoader } from '@betterment/js-runtime';
import { ErrorElement } from '@betterment/shared-components';
import { Navigate, type RouteObject } from 'react-router-dom';

import { NotFound } from './NotFound';

import AcceptAdvisedSolo401kInvitation from '../../AcceptAdvisedSolo401kInvitation';
import { AccountPage } from '../../account/AccountPage';
import { AccountSecurityPage } from '../../account-security/AccountSecurityPage';
import { ActivityPage } from '../../activity/ActivityPage';
import AddAccount from '../../add-account/Controller';
import { AccountIntroduction } from '../../add-account/self-directed-investing/introduction/Introduction';
import { InvestingExperience } from '../../add-account/self-directed-investing/investing-experience/InvestingExperience';
import { AccountOwnership } from '../../add-account/self-directed-investing/ownership/AccountOwnership';
import { JointAccountFlow } from '../../add-account/self-directed-investing/ownership/joint-account/JointAccountFlow';
import { UserDetails } from '../../add-account/self-directed-investing/ownership/joint-account/UserDetails';
import { UserSearch } from '../../add-account/self-directed-investing/ownership/joint-account/UserSearch';
import { TrustAccountFlow } from '../../add-account/self-directed-investing/ownership/trust/TrustAccountFlow';
import { TrustDetails } from '../../add-account/self-directed-investing/ownership/trust/TrustDetails';
import { AccountTermsAndAgreements } from '../../add-account/self-directed-investing/terms/AccountTermsAndAgreements';
import { ProductSelectionStep } from '../../add-account/steps/product-selection/ProductSelectionStep';
import { Controller as AdvisorNetworkApp } from '../../advisor-network/Controller';
import CheckingApp from '../../apps/CheckingApp';
import GenericErrorApp from '../../apps/GenericErrorApp';
import PerformanceRealtimeLoaderApp from '../../apps/PerformanceRealtimeLoaderApp';
import BuyOrderFlow from '../../buy-order-flow/Controller';
import { CustomPortfolioBuilderPage } from '../../CustomPortfolioBuilder/CustomPortfolioBuilderPage';
import DeferralSettingsApp from '../../DeferralSettingsApp/Controller';
import EditAddressPage from '../../edit-address-page/EditAddressPage';
import EditUserLevelSettings from '../../edit-user-level-settings/Controller';
import EmailVerificationCheckpointApp from '../../EmailVerificationCheckpoint';
import ExternallyAdvisedPlanFlow from '../../ExternallyAdvisedPlanFlow/Controller';
import { EchoRoute } from '../../features/echo/EchoRoute';
import FiveTwoNineOhioCDPurchaseApp from '../../FiveTwoNineOhioCDPurchaseApp';
import { ForecasterPage } from '../../forecaster/ForecasterPage';
import FourOhOneKSelectApp from '../../FourOhOneKAccountSelectApp';
import FourOhOneKDetailsApp from '../../FourOhOneKDetailsApp';
import Home from '../../home/home';
import IraDetailsApp from '../../IraDetailsApp';
import { AccountPerformancePage } from '../../performance/account/AccountPerformancePage';
import { UserPerformancePage } from '../../performance/user/UserPerformancePage';
import { PurposePage } from '../../purpose/PurposePage';
import { EmergencyFundFlow } from '../../purpose-flow/purpose-details/emergency-fund/FlowConfig';
import { EmergencyFundNameAndImageStep } from '../../purpose-flow/purpose-details/emergency-fund/steps/EmergencyFundNameAndImageStep';
import { MonthlyExpensesStep } from '../../purpose-flow/purpose-details/emergency-fund/steps/MonthlyExpensesStep';
import { MajorPurchaseFlow } from '../../purpose-flow/purpose-details/major-purchase/FlowConfig';
import { NameAndImageStep } from '../../purpose-flow/purpose-details/major-purchase/steps/NameAndImageStep';
import { TargetStep } from '../../purpose-flow/purpose-details/major-purchase/steps/TargetStep';
import { RetirementFlow } from '../../purpose-flow/purpose-details/retirement/FlowConfig';
import { DesiredSpendingStep } from '../../purpose-flow/purpose-details/retirement/steps/DesiredSpendingStep';
import { RetirementDetailStep } from '../../purpose-flow/purpose-details/retirement/steps/RetirementDetailStep';
import { RetirementNameAndImageStep } from '../../purpose-flow/purpose-details/retirement/steps/RetirementNameAndImageStep';
import { RetirementSelectionStep } from '../../purpose-flow/purpose-details/retirement/steps/RetirementSelectionStep';
import { PurposeSelectionFlow } from '../../purpose-flow/purpose-selection/FlowConfig';
import { AssignmentStep } from '../../purpose-flow/purpose-selection/steps/AssignmentStep';
import { SelectionStep } from '../../purpose-flow/purpose-selection/steps/SelectionStep';
import ReferAFriendPage from '../../ReferAFriend/components/ReferAFriendPage';
import { SecLendingEnrollmentInvite } from '../../SecLendingEnrollmentInvite/SecLendingEnrollmentInvite';
import SellOrderFlow from '../../sell-order-flow/Controller';
import FullPageLoader from '../../Shared/components/FullPageLoader';
import { TestTrackFeatureGate } from '../../Shared/components-v2/test-track/TestTrackFeatureGate';
import { urlBuilder } from '../../Shared/urlBuilder';
import echoRoutes from '../echo-routes.json';
import { loadInitialProps } from '../loadInitialProps';

type PangeaRouteObject = RouteObject & {
  feature: string; // this attribute is necessary for statsd reporting: https://github.com/Betterment/gems/commit/d344c4b0ef5c7f1396a15509eac5bd46de5bf8e7
};

const SELF_DIRECTED_INVESTING_ENABLED = 'retail.self_directed_investing_enabled';
const BONUS_DEFERRALS_ENABLED = 'retail.px_bonus_deferrals_q3_2025_enabled';
const REACTIFY_USER_LEVEL_PERFORMANCE = 'retail.user_level_performance_reactify_q3_2025_enabled';
const ADVISOR_NETWORK_ENABLED = 'retail.advisor_network_q4_2025_enabled';
const REACT_PRODUCT_SELECTION_PAGE_ENABLED = 'retail.react_product_selection_page_q1_2026_enabled';
const SDI_JOINT_AND_TRUSTS_ENABLED = 'retail.sdi_joint_and_trusts_flow_q1_2026_enabled';
const CUSTOM_PORTFOLIO_BUILDER_ENABLED = 'retail.custom_portfolio_builder_q1_2026_enabled';
const SEC_LENDING_ENROLLMENT_INVITE_ENABLED = 'retail.b4a_ax_advised_client_sec_lending_enabled';

const withFeatureGate = (
  element: ReactElement,
  featureGate: string,
  fallback: ReactNode = <NotFound />
): ReactElement => (
  <TestTrackFeatureGate name={featureGate} context="Routes">
    <TestTrackFeatureGate.True>{element}</TestTrackFeatureGate.True>
    <TestTrackFeatureGate.False>{fallback}</TestTrackFeatureGate.False>
  </TestTrackFeatureGate>
);

export const pangeaRoutes: PangeaRouteObject[] = [
  {
    path: '/',
    element: <Home />,
    feature: 'app_home',
  },
  {
    path: `${urlBuilder.getAppChecking.path}/*`,
    element: <CheckingApp />,
    feature: 'checking_home',
  },
  {
    path: urlBuilder.appPersonalReferralInvites.path,
    element: <ReferAFriendPage />,
    feature: 'content_referrals',
    loader: loadInitialProps,
    hydrateFallbackElement: <FullPageLoader />,
  },
  {
    path: `${urlBuilder.appFiveTwoNineOhioCDPurchase.path}/*`,
    element: <FiveTwoNineOhioCDPurchaseApp />,
    feature: 'investing_five_two_nine',
  },
  {
    path: `${urlBuilder.appExternallyAdvisedPlan.path}/*`,
    element: <ExternallyAdvisedPlanFlow />,
    feature: 'investing_edit_goal',
  },
  {
    path: urlBuilder.appIraDetails.path,
    element: <IraDetailsApp />,
    feature: 'investing_goals_retirement',
  },
  {
    path: urlBuilder.appFourOhOneKDetails.path,
    element: <FourOhOneKDetailsApp />,
    feature: 'investing_four_oh_one_k',
  },
  {
    path: urlBuilder.appFourOhOneKSelect.path,
    element: <FourOhOneKSelectApp />,
    feature: 'investing_four_oh_one_k',
  },
  {
    path: urlBuilder.appPersonalInfoEdit.path,
    element: <EditAddressPage />,
    feature: 'user_settings_profile_management',
    loader: loadInitialProps,
    hydrateFallbackElement: <FullPageLoader />,
  },
  {
    path: urlBuilder.appPurpose.path,
    element: <PurposePage />,
    feature: 'envelope_purpose',
  },
  {
    path: urlBuilder.appAccount.path,
    element: <AccountPage />,
    feature: 'envelope_account',
  },
  {
    path: urlBuilder.appCustomPortfolioBuilder.path,
    element: withFeatureGate(<CustomPortfolioBuilderPage />, CUSTOM_PORTFOLIO_BUILDER_ENABLED),
    feature: 'custom_portfolio_builder',
  },
  {
    path: `${urlBuilder.appPerformance.path}`,
    element: withFeatureGate(
      <UserPerformancePage />,
      REACTIFY_USER_LEVEL_PERFORMANCE,
      <PerformanceRealtimeLoaderApp />
    ),
    feature: 'performance',
  },
  {
    path: `${urlBuilder.appAccountPerformance.path}`,
    element: withFeatureGate(<AccountPerformancePage />, REACTIFY_USER_LEVEL_PERFORMANCE),
    feature: 'performance',
  },
  {
    path: urlBuilder.appActivity.path,
    element: <ActivityPage />,
    feature: 'investing_activity',
  },
  {
    path: urlBuilder.appAccountForecaster.path,
    element: <ForecasterPage />,
    feature: 'investing_forecaster',
  },
  {
    path: urlBuilder.appPurposeForecaster.path,
    element: <ForecasterPage />,
    feature: 'investing_forecaster',
  },
  {
    path: urlBuilder.appAccountSecurity.path,
    element: withFeatureGate(<AccountSecurityPage />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'account_security',
  },
  {
    path: urlBuilder.emailVerificationCheckpoint.path,
    element: <EmailVerificationCheckpointApp />,
    feature: 'user_settings_profile_contact_management',
  },
  {
    path: `${urlBuilder.appAccountBuyOrder.path}/*`,
    element: withFeatureGate(<BuyOrderFlow />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'direct_trading',
  },
  {
    path: `${urlBuilder.appAccountSellOrder.path}/*`,
    element: withFeatureGate(<SellOrderFlow />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'direct_trading',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/*`,
    element: <AddAccount />,
    feature: 'add_account',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/self_directed_investing/introduction`,
    element: withFeatureGate(<AccountIntroduction />, SDI_JOINT_AND_TRUSTS_ENABLED),
    feature: 'account_introduction',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/self_directed_investing/ownership`,
    element: withFeatureGate(<AccountOwnership />, SDI_JOINT_AND_TRUSTS_ENABLED),
    feature: 'account_ownership',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/self_directed_investing/investing_experience`,
    element: withFeatureGate(<InvestingExperience />, SDI_JOINT_AND_TRUSTS_ENABLED),
    feature: 'account_investing_experience',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/self_directed_investing/terms_and_agreements`,
    element: withFeatureGate(<AccountTermsAndAgreements />, SDI_JOINT_AND_TRUSTS_ENABLED),
    feature: 'account_terms_and_agreements',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/self_directed_investing/joint_taxable`,
    element: withFeatureGate(
      <Navigate
        to={`${urlBuilder.appAddAccount.path}/self_directed_investing/joint_taxable/${randomUUID()}/user_search`}
        replace={true}
      />,
      SDI_JOINT_AND_TRUSTS_ENABLED
    ),
    feature: 'add_joint_taxable_account',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/self_directed_investing/joint_taxable/:flowId`,
    element: withFeatureGate(<JointAccountFlow />, SDI_JOINT_AND_TRUSTS_ENABLED),
    children: [
      { path: 'user_search', element: <UserSearch /> },
      { path: 'user_details', element: <UserDetails /> },
    ],
    feature: 'add_joint_taxable_account',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/self_directed_investing/trust`,
    element: withFeatureGate(
      <Navigate
        to={`${urlBuilder.appAddAccount.path}/self_directed_investing/trust/${randomUUID()}/trust_details`}
        replace={true}
      />,
      SDI_JOINT_AND_TRUSTS_ENABLED
    ),
    feature: 'add_trust_account',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/self_directed_investing/trust/:flowId`,
    element: withFeatureGate(<TrustAccountFlow />, SDI_JOINT_AND_TRUSTS_ENABLED),
    children: [{ path: 'trust_details', element: <TrustDetails /> }],
    feature: 'add_trust_account',
  },
  {
    path: urlBuilder.appAddAccountProductSelection.path,
    element: withFeatureGate(<ProductSelectionStep />, REACT_PRODUCT_SELECTION_PAGE_ENABLED),
    feature: 'add_account',
  },
  {
    path: `${urlBuilder.appAdvisorNetwork.path}/*`,
    element: withFeatureGate(<AdvisorNetworkApp />, ADVISOR_NETWORK_ENABLED),
    feature: 'advisor_network',
  },
  {
    path: `${urlBuilder.appDeferralSettings.path}/*`,
    element: withFeatureGate(<DeferralSettingsApp />, BONUS_DEFERRALS_ENABLED),
    feature: 'investing_four_oh_one_k',
  },
  {
    path: '/generic_error',
    element: <GenericErrorApp />,
    feature: 'app_error_pages',
  },
  {
    path: `${urlBuilder.appEditUserLevelSettings.path}/*`,
    element: withFeatureGate(<EditUserLevelSettings />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'edit_user_level_settings',
  },
  {
    path: '/accept_solo_401k/*',
    element: <AcceptAdvisedSolo401kInvitation />,
    feature: 'advised_401k',
  },
  {
    path: urlBuilder.appPurposeSelection.path,
    element: <Navigate to="/purpose_selection/selection" replace={true} />,
    feature: 'purpose_selection',
  },
  {
    path: urlBuilder.appPurposeSelection.path,
    element: <PurposeSelectionFlow />,
    children: [
      { path: 'selection', element: <SelectionStep /> },
      { path: 'assignment', element: <AssignmentStep /> },
    ],
    feature: 'purpose_selection',
  },
  {
    path: urlBuilder.appAddMajorPurchasePurpose.path,
    element: <Navigate to={`/add_purpose/major_purchase/${randomUUID()}/name_and_image`} replace={true} />,
    feature: 'add_purpose',
  },
  {
    path: `${urlBuilder.appAddMajorPurchasePurpose.path}/:flowId`,
    element: <MajorPurchaseFlow />,
    children: [
      { path: 'name_and_image', element: <NameAndImageStep /> },
      { path: 'target_details', element: <TargetStep /> },
    ],
    feature: 'add_purpose',
  },
  {
    path: urlBuilder.appAddEmergencyFundPurpose.path,
    element: <Navigate to={`/add_purpose/emergency_fund/${randomUUID()}/name_and_image`} replace={true} />,
    feature: 'add_purpose',
  },
  {
    path: `${urlBuilder.appAddEmergencyFundPurpose.path}/:flowId`,
    element: <EmergencyFundFlow />,
    children: [
      { path: 'name_and_image', element: <EmergencyFundNameAndImageStep /> },
      { path: 'monthly_expenses', element: <MonthlyExpensesStep /> },
    ],
    feature: 'add_purpose',
  },
  {
    path: urlBuilder.appAddRetirementPurpose.path,
    element: <Navigate to={`/add_purpose/retirement/${randomUUID()}/name_and_image`} replace={true} />,
    feature: 'add_purpose',
  },
  {
    path: `${urlBuilder.appAddRetirementPurpose.path}/:flowId`,
    element: <RetirementFlow />,
    children: [
      { path: 'name_and_image', element: <RetirementNameAndImageStep /> },
      { path: 'retirement_selection', element: <RetirementSelectionStep /> },
      { path: 'retirement_details', element: <RetirementDetailStep /> },
      { path: 'desired_spending', element: <DesiredSpendingStep /> },
    ],
    feature: 'add_purpose',
  },
  {
    path: urlBuilder.appSecuritiesLendingEnrollmentInvitation.path,
    element: withFeatureGate(<SecLendingEnrollmentInvite />, SEC_LENDING_ENROLLMENT_INVITE_ENABLED),
    feature: 'sec_lending_enrollment_invitation',
  },
  ...echoRoutes.map(
    (route): PangeaRouteObject => ({
      path: route.path.replace('/:firm_key?/app/', '/'),
      feature: route.feature,
      loader: echoLoader,
      element: <EchoRoute key={route.path} />,
      errorElement: <ErrorElement owner="web_platform" feature={route.feature} />,
      hydrateFallbackElement: <FullPageLoader />,
    })
  ),
];
